import React from "react";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { styled } from "@mui/material/styles";
// ICONS
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
// COLORS
import { grey } from "@mui/material/colors";

export const LightGreyBox = styled(Box)(({ theme }) => ({
  padding: 12,
  borderRadius: 8,
  backgroundColor: grey["50"],
}));

export const LightDarkGreyBox = styled(Box)(({ theme, shadow }) => ({
  padding: 12,
  borderRadius: 8,
  backgroundColor: grey["100"],
  boxShadow: shadow ? "0 -2px 10px rgba(0,0,0,0.1)" : "none",
}));

export const LightDarkGreyBoxNoBackgroundOnPrint = styled(Box)(({ theme }) => ({
  padding: 12,
  borderRadius: 8,
  backgroundColor: grey["100"],
  "@media print": {
    padding: 4,
    backgroundColor: "transparent",
    pageBreakInside: "avoid",
  },
}));

export const DarkGreyBox = styled(Box)(({ theme }) => ({
  padding: 12,
  borderRadius: 8,
  backgroundColor: grey["300"],
}));

export const GreyButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText(grey[500]),
  backgroundColor: grey[100],
  "&:hover": {
    backgroundColor: grey[200],
  },
}));

export const GreyButtonNoShadow = styled(Button)(
  ({ theme, textColor, fontWeight }) => ({
    color: textColor || grey["900"],
    fontWeight: fontWeight || "normal",
    boxShadow: "none",
    backgroundColor: grey[100],
    "&:hover": {
      backgroundColor: grey[200],
    },
  })
);

export const ButtonLinkStyled = styled(Link)(({ theme, marginTop }) => ({
  padding: 4,
  display: "flex",
  justifyContent: "center",
  marginTop: marginTop ? marginTop : 0,
  color: grey["900"],
  borderRadius: 5,
  backgroundColor: grey["300"],
  "&:hover": {
    backgroundColor: grey["200"],
  },
}));

export const ButtonLinkLikeStyled = styled(Button)(({ theme, marginTop }) => ({
  padding: 4,
  display: "flex",
  justifyContent: "center",
  marginTop: marginTop ? marginTop : 0,
  color: grey["900"],
  borderRadius: 5,
  backgroundColor: grey["300"],
  "&:hover": {
    backgroundColor: grey["200"],
  },
}));

export const TypographyBoldOnPrint = styled(Typography)(
  ({ theme, color, printsize }) => ({
    color: color || grey["700"],
    "@media print": {
      color: grey["900"],
      letterSpacing: 0.34,
      fontWeight: "600",
      fontSize: printsize || 12,
    },
  })
);

export const TypographyGreyBoldOnPrint = styled(Typography)(
  ({ theme, color }) => ({
    color: color || grey["700"],
    "@media print": {
      color: grey["900"],
      letterSpacing: 0.34,
      fontWeight: "600",
    },
  })
);

export const BoxFullHeightOnPrint = styled(Box)(({ theme }) => ({
  "@media print": {
    overfow: "scroll",
    pageBreakInside: "avoid",
  },
}));

export const StyledStack = styled(Stack)(({ backgroundColor }) => ({
  backgroundColor: backgroundColor || grey[100],
  borderRadius: 4,
  flexDirection: "row",
  alignItems: "center",
  "& > *": {
    margin: "0 4px", // Adjust the spacing between items as needed
  },
}));

export const DividerBlackOnPrint = styled(Divider)(({ theme }) => ({
  borderStyle: "dotted",
  "@media print": {
    borderColor: grey["900"],
    borderStyle: "dotted",
  },
}));

export const LinkStyled = styled(Link)(({ theme, borderRight, fullWidth }) => ({
  width: fullWidth ? "100%" : "inherit",
  color: "#323232",
  display: "block",
  borderRadius: borderRight ? 0 : 5,
  borderRightStyle: borderRight ? "solid" : null,
  borderRightColor: borderRight ? grey["200"] : null,
  borderRightWidth: borderRight ? 1 : 0,
  "&:hover": {
    backgroundColor: grey["100"],
  },
}));

export const StyledAccordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} {...props} />
))(({ theme }) => ({
  borderTop: "none",
  borderLeft: "none",
  borderRight: "none",
  borderBottomWidth: 1,
  "&:before": {
    display: "none",
  },
}));

export const StyledAccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  my: 20,
  borderRadius: 10,
  backgroundColor: grey["50"],
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

export const StyledAccordionDetails = styled(MuiAccordionDetails)(
  ({ theme }) => ({
    borderTop: "none",
    borderRadius: 20,
    padding: theme.spacing(2),
  })
);
